import {
  EmailAuthProvider,
  GoogleAuthProvider,
  applyActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  deleteUser,
  onAuthStateChanged,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  reload,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithCustomToken,
  signOut as fbSignOut,
  updatePassword,
  updateProfile,
  verifyPasswordResetCode,
  type Unsubscribe,
  type User,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/core/firebase/client";
import { withRequestId } from "@/core/firebase/idempotency";

class AuthRepository {
  signInWithCustomToken(token: string): Promise<User> {
    return signInWithCustomToken(auth, token).then(credential => credential.user);
  }

  signIn(email: string, password: string): Promise<User> {
    return signInWithEmailAndPassword(auth, email, password).then(
      (cred) => cred.user,
    );
  }

  /** Create a new Firebase user with email + password, returning the User. */
  createUser(email: string, password: string): Promise<User> {
    return createUserWithEmailAndPassword(auth, email, password).then(
      (cred) => cred.user,
    );
  }

  /** Update the currently-signed-in user's display name / photo. */
  updateCurrentUserProfile(patch: { displayName?: string; photoURL?: string }): Promise<void> {
    if (!auth.currentUser) throw new Error("No authenticated user");
    return updateProfile(auth.currentUser, patch);
  }

  signInWithGoogle(): Promise<User> {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    return signInWithPopup(auth, provider).then((cred) => cred.user);
  }

  signOut(): Promise<void> {
    return fbSignOut(auth);
  }

  /**
   * Send the password-reset email. Routed through our own
   * `send_custom_password_reset_email` Cloud Function (not Firebase's
   * automatic sender) so it carries our branded HTML instead of the stock
   * template — Console's "Password reset" body is editable, but keeping
   * both auth emails on one pipeline is simpler than maintaining two. The
   * link still lands on our own `/auth/action?mode=resetPassword&oobCode=...`
   * route (server sets `handle_code_in_app: true`).
   *
   * Throws `functions/not-found` for an unknown email and
   * `functions/resource-exhausted` if called again within the server-side
   * cooldown — callers map both to friendly copy (see
   * forgot-password-page.tsx).
   */
  sendPasswordReset(email: string): Promise<void> {
    const fn = httpsCallable<
      { email: string; requestId: string },
      { sent: boolean }
    >(functions, "send_custom_password_reset_email");
    return fn(withRequestId({ email: email.trim().toLowerCase() })).then(() => undefined);
  }

  /** Confirm a password-reset code and set the new password. */
  async confirmPasswordReset(
    oobCode: string,
    newPassword: string,
  ): Promise<string> {
    const email = await verifyPasswordResetCode(auth, oobCode);
    await confirmPasswordReset(auth, oobCode, newPassword);
    return email;
  }

  /** Apply an action code (e.g. email verification, email change). */
  applyActionCode(oobCode: string): Promise<void> {
    return applyActionCode(auth, oobCode);
  }

  /**
   * Send a verification email to the current user, via
   * `send_custom_verification_email` — Firebase Console locks this
   * template's body ("to help prevent spam"), so this is the only way to
   * get our branded design onto it. No-ops silently if already verified.
   *
   * Throws `functions/resource-exhausted` if called again within the
   * server-side cooldown (see email-verification-page.tsx / account-modals.tsx
   * for the client-side cooldown UI this backs up).
   */
  async sendVerificationEmail(signup = false): Promise<void> {
    if (!auth.currentUser) throw new Error("No authenticated user");
    const fn = httpsCallable<{ requestId: string; signup?: boolean }, { sent: boolean }>(
      functions,
      "send_custom_verification_email",
    );
    await fn(withRequestId(signup ? { signup: true } : {}));
  }

  /**
   * Change the password of the currently-signed-in user. Re-authenticates
   * first with the current password (Firebase requires this for sensitive
   * operations) and then updates to the new one. Throws Firebase auth errors
   * unchanged so the UI can map `auth/wrong-password`, `auth/weak-password`,
   * `auth/requires-recent-login`, etc. to friendly Spanish copy.
   *
   * Only works for email/password accounts — Google-signed-in users have no
   * password to change and should manage it via Google. The `email` is read
   * from `auth.currentUser` rather than passed by the caller to avoid the
   * UI ever lying about which account is being mutated.
   */
  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error("No authenticated user");
    if (!user.email) {
      // Google-only / passwordless account.
      throw new Error("No email on this account; cannot change password");
    }
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  }

  /** Reload the Firebase user to pick up changes (e.g. emailVerified flag). */
  async reloadCurrentUser(): Promise<User | null> {
    if (!auth.currentUser) return null;
    await reload(auth.currentUser);
    return auth.currentUser;
  }

  /** Re-authenticate the email/password user with their current password.
   *  Used as a guard before account deletion. */
  async reauthenticateWithCurrentPassword(currentPassword: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error("No authenticated user");
    if (!user.email) throw new Error("No email on this account");
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
  }

  /** Re-authenticate a Google-signed-in user via the popup flow. */
  async reauthenticateWithGoogle(): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error("No authenticated user");
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    await reauthenticateWithPopup(user, provider);
  }

  /**
   * Permanently delete the currently-signed-in Firebase Auth user. Caller
   * must re-authenticate first; otherwise Firebase rejects with
   * `auth/requires-recent-login`.
   *
   * This deletes the AUTH identity only — Firestore data tied to the user
   * (staff records, CRM rows where they're listed, audit logs) is purged
   * by the `delete-user-data` Firebase extension on the prod project. See
   * [next-prod-release.md](../../../docs/next-prod-release.md) — the extension
   * must be installed before this method is exposed in prod.
   *
   * For owners we route to a different flow (request club deletion via
   * Settings → Perfil → Zona de peligro) to avoid orphaning a club. The UI
   * gates the menu item accordingly.
   */
  async deleteAccount(): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error("No authenticated user");
    await deleteUser(user);
  }

  /** True when the current user signed in with the email/password provider. */
  hasPasswordProvider(): boolean {
    return (
      auth.currentUser?.providerData.some((p) => p.providerId === "password") ?? false
    );
  }

  /** True when the current user has a Google identity linked. */
  hasGoogleProvider(): boolean {
    return (
      auth.currentUser?.providerData.some((p) => p.providerId === "google.com") ?? false
    );
  }

  observe(callback: (user: User | null) => void): Unsubscribe {
    return onAuthStateChanged(auth, callback);
  }

  get currentUser(): User | null {
    return auth.currentUser;
  }
}

export const authRepository = new AuthRepository();
